import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import config from 'constants/apiConfig';
import { selectGroupGuid, selectUserGuid } from 'slices/commonSelectors';
import { useCnicFlag } from 'utils/featureFlags';
import {
  CNIC_HAWAII_EVENT_REGION,
  CNIC_HAWAII_EVENT_STARTED_AT,
  CNIC_HAWAII_EVENT_SUMMARY,
  CNIC_HAWAII_FACILITIES,
  CNIC_HAWAII_LINKED_EVENT_NAME,
  CNIC_HAWAII_ORG_EVENT_DEMO_KEY,
  CNIC_HAWAII_RESOURCES_COMMITTED,
  CNIC_HAWAII_THRESHOLD_DESCRIPTION,
  type CnicHawaiiFacility,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/cnicHawaiiThreatNotification';
import { orgEventKeys, type OrgEvent } from 'hooks/orgEvents/orgEventHooks';

const CNIC_EVENT_TYPE = 'Tropical Storm';

const ensureLocks = new Map<string, Promise<unknown>>();

async function invalidateOrgEventList(
  queryClient: ReturnType<typeof useQueryClient>,
  groupGuid: string
): Promise<void> {
  await queryClient.invalidateQueries({
    queryKey: orgEventKeys.list(groupGuid),
  });
  await queryClient.invalidateQueries({
    queryKey: [...orgEventKeys.list(groupGuid), true],
  });
}

/** Oahu installations only so the event pin sits over Navy Region Hawaii HQ. */
function cnicOahuCentroid(sites: CnicHawaiiFacility[]): {
  lon: number;
  lat: number;
} {
  const oahu = sites.filter((site) => site.state === 'HI' && site.lon > -159);
  const source = oahu.length > 0 ? oahu : sites;
  if (source.length === 0) {
    return { lon: -157.9448, lat: 21.3547 };
  }
  const lon = source.reduce((sum, f) => sum + f.lon, 0) / source.length;
  const lat = source.reduce((sum, f) => sum + f.lat, 0) / source.length;
  return { lon, lat };
}

async function patchCnicOrgEventIfStale(
  existing: OrgEvent,
  queryClient: ReturnType<typeof useQueryClient>
): Promise<OrgEvent> {
  const needsPatch =
    existing.name !== CNIC_HAWAII_LINKED_EVENT_NAME ||
    existing.summary !== CNIC_HAWAII_EVENT_SUMMARY ||
    existing.resources_committed !== CNIC_HAWAII_RESOURCES_COMMITTED ||
    existing.region !== CNIC_HAWAII_EVENT_REGION ||
    existing.type !== CNIC_EVENT_TYPE ||
    existing.threshold_description !== CNIC_HAWAII_THRESHOLD_DESCRIPTION;
  if (!needsPatch) {
    return existing;
  }

  const patched = await axios.patch<OrgEvent>(
    `${config.apiGateway.org_events}/${existing.id}`,
    {
      name: CNIC_HAWAII_LINKED_EVENT_NAME,
      summary: CNIC_HAWAII_EVENT_SUMMARY,
      resources_committed: CNIC_HAWAII_RESOURCES_COMMITTED,
      region: CNIC_HAWAII_EVENT_REGION,
      type: CNIC_EVENT_TYPE,
      threshold_description: CNIC_HAWAII_THRESHOLD_DESCRIPTION,
    }
  );
  await invalidateOrgEventList(queryClient, existing.group_guid);
  return patched.data;
}

async function ensureCnicOrgEventForGroup({
  groupGuid,
  userGuid,
  queryClient,
}: {
  groupGuid: string;
  userGuid: string;
  queryClient: ReturnType<typeof useQueryClient>;
}): Promise<OrgEvent> {
  const resp = await axios.get<OrgEvent[]>(config.apiGateway.org_events, {
    params: { groupGuid, includeArchived: true },
  });
  const existing = (resp.data ?? []).find(
    (event) => event.demo_key === CNIC_HAWAII_ORG_EVENT_DEMO_KEY
  );
  if (existing) {
    return patchCnicOrgEventIfStale(existing, queryClient);
  }

  const { lon, lat } = cnicOahuCentroid(CNIC_HAWAII_FACILITIES);
  const created = await axios.post<OrgEvent>(config.apiGateway.org_events, {
    group_guid: groupGuid,
    name: CNIC_HAWAII_LINKED_EVENT_NAME,
    type: CNIC_EVENT_TYPE,
    status: 'Active',
    severity: 'High',
    region: CNIC_HAWAII_EVENT_REGION,
    business_unit: 'CNIC',
    summary: CNIC_HAWAII_EVENT_SUMMARY,
    resources_committed: CNIC_HAWAII_RESOURCES_COMMITTED,
    lon,
    lat,
    started_at: CNIC_HAWAII_EVENT_STARTED_AT,
    creation_kind: 'threshold',
    created_by_user_guid: userGuid,
    threshold_description: CNIC_HAWAII_THRESHOLD_DESCRIPTION,
    demo_key: CNIC_HAWAII_ORG_EVENT_DEMO_KEY,
  });

  await invalidateOrgEventList(queryClient, groupGuid);

  return created.data;
}

export function useEnsureCnicOrgEvent() {
  const cnicEnabled = useCnicFlag();
  const groupGuid = useSelector(selectGroupGuid);
  const userGuid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const completedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!cnicEnabled || !groupGuid || !userGuid) {
      return;
    }
    if (completedRef.current === groupGuid) {
      return;
    }

    const lockKey = `cnic-org-event:${groupGuid}`;
    let cancelled = false;

    const run = async () => {
      const existing = ensureLocks.get(lockKey);
      if (existing) {
        await existing;
        if (!cancelled) completedRef.current = groupGuid;
        return;
      }

      const promise = ensureCnicOrgEventForGroup({
        groupGuid,
        userGuid,
        queryClient,
      }).finally(() => {
        ensureLocks.delete(lockKey);
      });
      ensureLocks.set(lockKey, promise);

      try {
        await promise;
        if (!cancelled) completedRef.current = groupGuid;
      } catch (error) {
        console.error('Failed to ensure CNIC Hawaii org event', error);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [cnicEnabled, groupGuid, userGuid, queryClient]);
}
